/**
 * Zahlungsdialoge V2 – reine Darstellungslogik (ohne React). Alle Beträge in Cent (Regel 5).
 *
 * Hier wird nichts kassiert und keine Zahlung gestartet: Die Funktionen leiten nur aus dem
 * Stand, den die bestehenden Dialoge (BarDialog, ZahlungDialog) schon berechnen, den
 * angezeigten Zustand, die Schnellwahl, die Stückelung und die gesperrten Tasten ab.
 */
import type { Zahlungsstatus } from '../../api';
import { gegebenVorschlaege, stueckelung } from '../../rueckgeld';
import { formatiereEuro } from '../../warenkorb';
import { ziffernTaste, type ZiffernTaste } from '../../ziffern';

// ── Barzahlung ──────────────────────────────────────────────────────────────────────────────

export interface SchnellBetrag {
  cent: number;
  text: string;
  passend: boolean;
}

/** Höchstzahl der Schnellbeträge neben „Passend“ (Figma FINAL-B1: Passend, 25, 30, 50, 100 €). */
export const SCHNELLBETRAEGE = 4;

/**
 * Schnellauswahl: „Passend“ und bis zu vier Beträge aus den bestehenden Vorschlägen
 * (`gegebenVorschlaege`), bevorzugt glatte Scheinbeträge (Vielfache von 5 €); reichen die
 * nicht, folgen die übrigen Vorschläge (z. B. volle Euro).
 */
export function schnellauswahl(summeCent: number): SchnellBetrag[] {
  const vorschlaege = gegebenVorschlaege(summeCent);
  if (vorschlaege.length === 0) {
    return [];
  }
  const ueber = vorschlaege.filter((c) => c > summeCent);
  const glatt = ueber.filter((c) => c % 500 === 0);
  const rest = ueber.filter((c) => c % 500 !== 0);
  const betraege = [...glatt, ...rest].slice(0, SCHNELLBETRAEGE).sort((a, b) => a - b);
  return [
    { cent: summeCent, text: 'Passend', passend: true },
    ...betraege.map((cent) => ({ cent, text: euroKurz(cent), passend: false })),
  ];
}

/** „25 €“ statt „25,00 €“ bei glatten Euro, sonst „24,50 €“. */
export function euroKurz(cent: number): string {
  return cent % 100 === 0 ? `${formatiereEuro(cent).slice(0, -3)} €` : `${formatiereEuro(cent)} €`;
}

/**
 * Touch-Ziffernblock im Bar-Dialog: wie der Ziffernblock der Kasse von rechts eingeschoben
 * („5“ „0“ „00“ → 50,00 €), ausgehend vom aktuell gegebenen Betrag. Ergibt 0 → leeres Feld
 * (= passend), damit „Löschen“ und Rücktaste wieder den Ausgangszustand herstellen.
 */
export function zifferEingabe(gegebenCent: number | null, taste: ZiffernTaste): number | null {
  const alt =
    gegebenCent !== null && Number.isInteger(gegebenCent) && gegebenCent > 0 ? gegebenCent : 0;
  const neu = ziffernTaste(alt, taste);
  return neu <= 0 ? null : neu;
}

/** Scheine und Münzen zum Addieren (Figma FINAL-B3, ohne bunte Geldgrafiken). */
export const SCHEINE_V2: readonly number[] = [500, 1_000, 2_000, 5_000, 10_000, 20_000];
export const MUENZEN_V2: readonly number[] = [200, 100, 50, 20, 10];

export type BarZustand =
  | 'start'
  | 'rueckgeld'
  | 'zu_niedrig'
  | 'ungueltig'
  | 'unplausibel'
  | 'verarbeitung'
  | 'ungeklaert';

/**
 * Angezeigter Zustand des Bar-Dialogs aus den Werten, die BarDialog bereits berechnet
 * (`unlesbar`, `plausibel`, `rueckgeld`, `laeuft`, `ungeklaert`). Die Freigabe des Kassierens
 * bleibt allein bei `kassierbar` aus BarDialog.
 */
export function barZustand(e: {
  ungeklaert: boolean;
  laeuft: boolean;
  unlesbar: boolean;
  plausibel: boolean;
  gegebenCent: number | null;
  rueckgeldCent: number | null;
}): BarZustand {
  // Während der Verarbeitung zuerst „verarbeitet“: Verkauf setzt den Vorgangsmerker (und damit
  // `ungeklaert`) schon vor dem Senden – erst ohne Antwort ist der Verkauf wirklich ungeklärt.
  if (e.laeuft) {
    return 'verarbeitung';
  }
  if (e.ungeklaert) {
    return 'ungeklaert';
  }
  if (e.unlesbar) {
    return 'ungueltig';
  }
  if (!e.plausibel) {
    return 'unplausibel';
  }
  if (e.gegebenCent === null) {
    return 'start';
  }
  return e.rueckgeldCent === null ? 'zu_niedrig' : 'rueckgeld';
}

/** Fehlbetrag in Cent (> 0), wenn zu wenig gegeben wurde; sonst null. */
export function fehlbetragCent(summeCent: number, gegebenCent: number | null): number | null {
  if (gegebenCent === null || !Number.isInteger(gegebenCent) || gegebenCent >= summeCent) {
    return null;
  }
  return summeCent - gegebenCent;
}

/** Stückelung als Text: „1 × 20 € · 1 × 5 € · 1 × 0,20 €“ (leer bei 0). */
export function stueckelungText(cent: number): string {
  return stueckelung(cent)
    .map((s) => `${String(s.anzahl)} × ${euroKurz(s.wertCent)}`)
    .join(' · ');
}

// ── Tastensperre ────────────────────────────────────────────────────────────────────────────

/**
 * Codex-Prüfung ZD-V2 (M1): F4 (Parken) und F6 (Teilen) verändern den Bon hinter dem Dialog –
 * Parken leert ihn, Teilen rechnet den Betrag neu – während der Zahlungsdialog offen bleibt.
 * Im V2-Dialog sind sie deshalb immer wirkungslos.
 */
export const BON_TASTEN: readonly string[] = ['F4', 'F6'];

/** Meldung zu einer abgefangenen Taste (F4/F6 eigene Begründung). */
export function sperrMeldung(regel: Tastenregel, taste: string): string {
  if (BON_TASTEN.includes(taste)) {
    return 'Parken (F4) und Teilen (F6) sind im Zahlungsdialog ohne Wirkung – der Bon bleibt unverändert.';
  }
  return regel.sperrText;
}

export interface Tastenregel {
  /** Tasten (KeyboardEvent.key), die der Dialog abfängt – sie erreichen die Kasse nicht. */
  gesperrt: readonly string[];
  /** Hinweis im Betragsband (dauerhaft sichtbar). */
  hinweis: string;
  /** Meldung, wenn eine gesperrte Taste gedrückt wurde. */
  sperrText: string;
}

/**
 * Tasten im Bar-Dialog (Figma FINAL-N, Bestandsaufnahme Abschnitt 8 Nr. 2 und 12):
 * - F3 ist bei offenem Bar-Dialog immer ohne Wirkung (keine Karte daneben),
 * - während der Verarbeitung sind zusätzlich Esc und F2 gesperrt (Esc schlösse sonst den
 *   Dialog, während der Beleg weiterläuft); Enter ist dann ohnehin wirkungslos.
 */
export function barTasten(zustand: BarZustand): Tastenregel {
  if (zustand === 'verarbeitung') {
    return {
      gesperrt: ['Escape', 'F2', 'F3', ...BON_TASTEN],
      hinweis: 'Esc · Enter · F2 · F3 gesperrt – Verarbeitung läuft',
      sperrText: 'Gesperrt, solange die Zahlung verarbeitet wird – bitte warten.',
    };
  }
  if (zustand === 'ungeklaert') {
    return {
      gesperrt: ['F3', ...BON_TASTEN],
      hinweis: 'Enter = Beleg prüfen · Esc schließt – Kassieren bleibt gesperrt',
      sperrText: 'Kartenzahlung gesperrt – zuerst den Belegstatus prüfen.',
    };
  }
  return {
    gesperrt: ['F3', ...BON_TASTEN],
    hinweis:
      zustand === 'start' || zustand === 'rueckgeld'
        ? 'Enter bestätigt · Esc schließt · F3 hier ohne Wirkung'
        : 'Betrag prüfen · Esc schließt · F3 hier ohne Wirkung',
    sperrText: 'F3 (Karte) ist im Bar-Dialog ohne Wirkung.',
  };
}

// ── Kartenzahlung ───────────────────────────────────────────────────────────────────────────

export type KartenZustand =
  | 'warten'
  | 'terminal'
  | 'unklar'
  | 'unterbrochen'
  | 'meldung'
  | 'bezahlt'
  | 'abgelehnt'
  | 'abgebrochen'
  | 'erstattet';

/**
 * Angezeigter Zustand der Kartenzahlung aus dem Stand, den ZahlungDialog abfragt. Nur der
 * Endzustand der API zählt; „unklar“, „unterbrochen“ und „meldung“ sind alle noch
 * `ausstehend` – die Zahlung bleibt offen und wird nie neu gestartet.
 */
export function kartenZustand(e: {
  status: Zahlungsstatus;
  terminalText: string | null;
  klaerungsgrund: string | null;
  klaerbar: boolean;
  meldung: string | null;
  unterbrechung: string | null;
}): KartenZustand {
  switch (e.status) {
    case 'bezahlt':
      return 'bezahlt';
    case 'abgelehnt':
      return 'abgelehnt';
    case 'storniert':
      return 'abgebrochen';
    case 'erstattet':
      return 'erstattet';
    case 'ausstehend':
      break;
  }
  if (e.klaerungsgrund !== null || e.klaerbar) {
    return 'unklar';
  }
  if (e.unterbrechung !== null) {
    return 'unterbrochen';
  }
  if (e.meldung !== null) {
    return 'meldung';
  }
  return e.terminalText === null ? 'warten' : 'terminal';
}

/** Solange die Zahlung `ausstehend` ist, sind Esc, F2 und F3 im Kartendialog gesperrt. */
export function kartenTasten(zustand: KartenZustand): Tastenregel {
  switch (zustand) {
    case 'warten':
    case 'terminal':
      return {
        gesperrt: ['Escape', 'F2', 'F3', ...BON_TASTEN],
        hinweis: 'Esc · F2 · F3 gesperrt – Zahlung läuft',
        sperrText: 'Gesperrt, solange die Kartenzahlung läuft.',
      };
    case 'unklar':
    case 'unterbrochen':
    case 'meldung':
      return {
        gesperrt: ['Escape', 'F2', 'F3', ...BON_TASTEN],
        hinweis: 'Esc ohne Wirkung · F2/F3 gesperrt bis zur Klärung',
        sperrText:
          'Gesperrt, bis das Ergebnis der Kartenzahlung geklärt ist – nicht erneut starten.',
      };
    case 'bezahlt':
      return {
        gesperrt: ['Escape', 'F2', 'F3', ...BON_TASTEN],
        hinweis: 'Beleg wird erstellt – Tasten gesperrt',
        sperrText: 'Gesperrt, solange der Beleg erstellt wird.',
      };
    case 'abgelehnt':
    case 'abgebrochen':
    case 'erstattet':
      return { gesperrt: BON_TASTEN, hinweis: 'Kein Beleg aus dieser Zahlung', sperrText: '' };
  }
}

export type SchrittStand = 'erledigt' | 'aktiv' | 'warnung' | 'fehler' | 'offen';

export interface KartenSchritt {
  name: 'Start' | 'Terminal' | 'Bezahlt' | 'Beleg';
  stand: SchrittStand;
}

/** Ablauf rechts im Kartendialog: Start → Terminal → Bezahlt → Beleg. */
export function kartenSchritte(zustand: KartenZustand): KartenSchritt[] {
  const s = (
    terminal: SchrittStand,
    bezahlt: SchrittStand,
    beleg: SchrittStand,
  ): KartenSchritt[] => [
    { name: 'Start', stand: 'erledigt' },
    { name: 'Terminal', stand: terminal },
    { name: 'Bezahlt', stand: bezahlt },
    { name: 'Beleg', stand: beleg },
  ];
  switch (zustand) {
    case 'warten':
      return s('aktiv', 'offen', 'offen');
    case 'terminal':
      return s('erledigt', 'aktiv', 'offen');
    case 'unklar':
    case 'unterbrochen':
    case 'meldung':
      return s('erledigt', 'warnung', 'offen');
    case 'bezahlt':
      return s('erledigt', 'erledigt', 'aktiv');
    case 'abgelehnt':
    case 'abgebrochen':
      return s('erledigt', 'fehler', 'offen');
    case 'erstattet':
      return s('erledigt', 'erledigt', 'offen');
  }
}

// ── Kartenstatus vor und nach dem Kartendialog (ZD-V2-F1) ──────────────────────────────────

/**
 * Zustände der Kartenzahlung, die `Verkauf.tsx` außerhalb von `ZahlungDialog` führt (Figma
 * FINAL-K1/K4/K6/K7). Die V2-Hülle zeigt sie nur an; sie leiten sich aus dem bestehenden
 * Zustand ab (Start läuft, `kartenStand`, `startUnklarSeit`) und ändern keinen Ablauf:
 * - `start`: Startaufruf unterwegs (ohne Bestätigungsschritt – der Start ist schon ausgelöst),
 * - `beleg`: bezahlt, der Beleg wird erstellt,
 * - `abgelehnt` / `abgebrochen`: kein Beleg,
 * - `start_unklar`: Antwort zum Start fehlt, erst „Zahlung prüfen“.
 */
export type KartenStatusArt = 'start' | 'beleg' | 'abgelehnt' | 'abgebrochen' | 'start_unklar';

const ALLE_SPERRTASTEN: readonly string[] = ['Escape', 'F2', 'F3', ...BON_TASTEN];

export function kartenStatusTasten(art: KartenStatusArt): Tastenregel {
  switch (art) {
    case 'start':
      return {
        gesperrt: ALLE_SPERRTASTEN,
        hinweis: 'Esc · F2 · F3 gesperrt – Zahlung wird gestartet',
        sperrText: 'Gesperrt, solange die Kartenzahlung gestartet wird – nicht erneut starten.',
      };
    case 'beleg':
      return {
        gesperrt: ALLE_SPERRTASTEN,
        hinweis: 'Beleg wird erstellt – Tasten gesperrt',
        sperrText: 'Gesperrt, solange der Beleg erstellt wird.',
      };
    case 'start_unklar':
      return {
        gesperrt: ALLE_SPERRTASTEN,
        hinweis: 'Esc ohne Wirkung · F2/F3 gesperrt bis zur Klärung',
        sperrText:
          'Gesperrt, bis der Kartenzahlungsstart geklärt ist – erst „Zahlung prüfen“, nicht erneut starten.',
      };
    case 'abgelehnt':
    case 'abgebrochen':
      // F2/F3 wirken wie die Tasten „Bar zahlen“/„Erneut mit Karte“ (Kürzel des Verkaufs)
      return { gesperrt: BON_TASTEN, hinweis: 'Esc schließt · kein Beleg', sperrText: '' };
  }
}

/** Ablauf rechts (Start → Terminal → Bezahlt → Beleg) für die Kartenstatus-Anzeige. */
export function kartenStatusSchritte(art: KartenStatusArt): KartenSchritt[] {
  switch (art) {
    case 'start':
      return [
        { name: 'Start', stand: 'aktiv' },
        { name: 'Terminal', stand: 'offen' },
        { name: 'Bezahlt', stand: 'offen' },
        { name: 'Beleg', stand: 'offen' },
      ];
    case 'start_unklar':
      return [
        { name: 'Start', stand: 'erledigt' },
        { name: 'Terminal', stand: 'warnung' },
        { name: 'Bezahlt', stand: 'offen' },
        { name: 'Beleg', stand: 'offen' },
      ];
    case 'beleg':
      return kartenSchritte('bezahlt');
    case 'abgelehnt':
    case 'abgebrochen':
      return kartenSchritte(art);
  }
}
